import { useEffect, useRef, useState } from '@wordpress/element';
import { Link } from 'react-router-dom';
import ShareButton from '../ShareButton';
import { usePlayer } from './playerContext';
import { Icon } from '../Icons';
import { trackEvent } from '../../utils/analytics';
import { useCastPlayer } from './useCastPlayer';
import { useAirPlay } from './useAirPlay';
import { useGiveaway } from '../../features/giveaways/GiveawayProvider';

export default function MusicPlayer() {
    const restUrl = window.bajantubeMusic?.restUrl || '/wp-json/bajantube/v1';
    const nonce = window.bajantubeMusic?.nonce || '';
    const positionStorageKey = 'bajantube_music_player_position_v1';

    const {
        audioRef,
        isOpen,
        isPlaying,
        type,
        playlistTitle,
        playlistSubtitle,
        currentTrack,
        currentIndex,
        tracks,
        hasNext,
        hasPrevious,
        listenId,
        listenToken,
        listenCompleted,
        maxPercent,
        playbackId,
        togglePlay,
        play,
        next,
        previous,
        closePlayer,
        updateListenSession,
        resetListenSession,
        removeFromQueue,
        playQueueIndex,
    } = usePlayer();
    const { showRewardToken } = useGiveaway();

    const [isQueueOpen, setIsQueueOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const [hasSeeked, setHasSeeked] = useState(false);
    const [timeCode, setTimeCode] = useState({
        current: '0:00',
        duration: '0:00',
    });

    const completeRequestSentRef = useRef(false);
    const progressMilestonesRef = useRef(new Set());
    const progressTimerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const restoredPositionTrackRef = useRef(null);
    const latestPlaybackTimeRef = useRef(0);
    const lastPreviousClickAtRef = useRef(0);
    const ignorePreviousClickUntilRef = useRef(0);
    const playerRef = useRef(null);
    const activeTrackIdRef = useRef(null);
    const activePlaybackIdRef = useRef(null);
    const listenStartAbortRef = useRef(null);
    const restoringPositionRef = useRef(false);
    const previousRestartThreshold = 5;
    const previousDoubleClickWindow = 700;

    const {
        castAvailable,
        castConnected,
        castIsPlaying,
        toggleCast,
        stopCasting,
        toggleCastPlayback,
        seekCast,
    } = useCastPlayer({
        audioRef,
        currentTrack,
        currentIndex,
        tracks,
        hasNext,
        next,
        onBrowserResume: () => {
            play();
        },
        onProgress: ({ percent, current, duration, currentTime }) => {
            latestPlaybackTimeRef.current = currentTime;
            setProgress(percent);
            setTimeCode({ current, duration });
            savePlaybackPosition(currentTime);
        },
        onEnded: () => {
            clearPlaybackPosition();
            sendFinalProgress();
        },
    });

    const {
        airPlayAvailable,
        airPlayConnected,
        airPlayIsPlaying,
        openAirPlayPicker,
    } = useAirPlay(audioRef, currentTrack?.id);

    useEffect(() => {
        function updateVisualProgress() {
            if (castConnected) {
                return;
            }

            const audio = audioRef.current;

            if (audio && audio.duration) {
                const percent = (audio.currentTime / audio.duration) * 100;

                setProgress(Math.min(100, Math.max(0, percent)));
                setTimeCode({
                    current: formatTime(audio.currentTime),
                    duration: formatTime(audio.duration),
                });

                savePlaybackPosition(audio.currentTime);
            }

            if (isPlaying) {
                animationFrameRef.current = requestAnimationFrame(updateVisualProgress);
            }
        }

        if (isPlaying && !castConnected) {
            animationFrameRef.current = requestAnimationFrame(updateVisualProgress);
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, currentTrack?.id, castConnected]);

    function getHeaders() {
        return {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
        };
    }

    function getCurrentPercent() {
        const audio = audioRef.current;

        if (!audio || !audio.duration) {
            return 0;
        }

        return Math.min(
            100,
            Math.max(0, (audio.currentTime / audio.duration) * 100)
        );
    }

    function getNextMaxPercent(percent) {
        return Math.max(maxPercent || 0, percent);
    }

    function getProgressMilestones() {
        return [25, 50, 75, 90];
    }

    function getNextProgressMilestone(percent) {
        return getProgressMilestones().find(
            (milestone) =>
                percent >= milestone &&
                !progressMilestonesRef.current.has(milestone)
        );
    }

    function formatTime(seconds) {
        if (isNaN(seconds) || !isFinite(seconds)) return '0:00';

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }

    function getSavedPlaybackPosition(trackId) {
        if (typeof window === 'undefined' || !trackId) {
            return 0;
        }

        try {
            const saved = window.localStorage.getItem(positionStorageKey);

            if (!saved) {
                return 0;
            }

            const parsed = JSON.parse(saved);

            if (parsed?.trackId !== trackId) {
                return 0;
            }

            return Number(parsed.currentTime || 0);
        } catch (error) {
            return 0;
        }
    }

    function savePlaybackPosition(currentTime = 0) {
        if (typeof window === 'undefined' || !currentTrack?.id) {
            return;
        }

        try {
            window.localStorage.setItem(
                positionStorageKey,
                JSON.stringify({
                    trackId: currentTrack.id,
                    currentTime,
                    updatedAt: Date.now(),
                })
            );
        } catch (error) {
            // Ignore storage failures.
        }
    }

    function clearPlaybackPosition() {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            window.localStorage.removeItem(positionStorageKey);
        } catch (error) {
            // Ignore storage failures.
        }
    }

    async function startListenSession(track, activePlaybackId, signal) {
        if (!track?.id) {
            return;
        }

        try {
            const response = await fetch(
                `${restUrl}/music/tracks/${track.id}/listen/start`,
                {
                    method: 'POST',
                    signal,
                    headers: getHeaders(),
                    body: JSON.stringify({
                        source_type: type || '',
                        source_name: playlistTitle || '',
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                console.error('Failed to start listen:', result);
                return;
            }

            if (
                signal?.aborted
                || activeTrackIdRef.current !== track.id
                || activePlaybackIdRef.current !== activePlaybackId
            ) {
                return;
            }

            updateListenSession({
                listenId: result.listen_id,
                listenToken: result.listen_token,
                listenCompleted: false,
                maxPercent: 0,
            });
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Failed to start listen:', error);
        }
    }

    async function updateListenProgress(percent, seeked = hasSeeked) {
        if (!currentTrack?.id || !listenId || !listenToken) {
            return;
        }

        const nextMaxPercent = getNextMaxPercent(percent);

        updateListenSession({
            maxPercent: nextMaxPercent,
        });

        try {
            await fetch(
                `${restUrl}/music/tracks/${currentTrack.id}/listen/progress`,
                {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({
                        listen_id: listenId,
                        listen_token: listenToken,
                        listen_percent: nextMaxPercent,
                        has_seeked: seeked,
                    }),
                }
            );
        } catch (error) {
            console.error('Failed to update listen progress:', error);
        }
    }

    async function completeListen(percent, seeked = hasSeeked) {
        if (!currentTrack?.id || !listenId || !listenToken) {
            return;
        }

        const completedTrackId = currentTrack.id;
        const completedPlaybackId = playbackId;
        const completedListenId = listenId;
        const completedListenToken = listenToken;
        const nextMaxPercent = getNextMaxPercent(percent);

        updateListenSession({
            maxPercent: nextMaxPercent,
        });

        if (listenCompleted) {
            await updateListenProgress(nextMaxPercent, seeked);
            return;
        }

        try {
            const response = await fetch(
                `${restUrl}/music/tracks/${completedTrackId}/listen/complete`,
                {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({
                        listen_id: completedListenId,
                        listen_token: completedListenToken,
                        listen_percent: nextMaxPercent,
                        has_seeked: seeked,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                console.error('Failed to complete listen:', result);
                return;
            }

            const isCurrentTrack = (
                activeTrackIdRef.current === completedTrackId
                && activePlaybackIdRef.current === completedPlaybackId
            );

            if (result.completed) {
                if (isCurrentTrack) {
                updateListenSession({
                    listenCompleted: true,
                    maxPercent: result.max_percent || nextMaxPercent,
                });
                }

                if (result.giveawayReward) {
                    showRewardToken(result.giveawayReward);
                }

            } else if (isCurrentTrack) {
                updateListenSession({
                    maxPercent: result.max_percent || nextMaxPercent,
                });
            }
        } catch (error) {
            console.error('Failed to complete listen:', error);
        }
    }

    function sendFinalProgress() {
        if (!currentTrack?.id || !listenId || !listenToken) {
            return;
        }

        const percent = getCurrentPercent();
        const finalPercent = getNextMaxPercent(percent);

        const endpoint =
            finalPercent >= 90 && !hasSeeked && !listenCompleted
                ? 'complete'
                : 'progress';

        fetch(
            `${restUrl}/music/tracks/${currentTrack.id}/listen/${endpoint}`,
            {
                method: 'POST',
                keepalive: true,
                headers: getHeaders(),
                body: JSON.stringify({
                    listen_id: listenId,
                    listen_token: listenToken,
                    listen_percent: finalPercent,
                    has_seeked: hasSeeked,
                }),
            }
        ).catch(() => { });
    }

    useEffect(() => {
        const audio = audioRef.current;

        if (!audio || !currentTrack?.audioUrl) {
            return;
        }

        completeRequestSentRef.current = false;
        progressMilestonesRef.current = new Set();
        activeTrackIdRef.current = currentTrack.id;
        activePlaybackIdRef.current = playbackId;

        listenStartAbortRef.current?.abort();
        const listenController = new AbortController();
        listenStartAbortRef.current = listenController;
        let restoreTimeout = null;
        let restoreGuardTimeout = null;

        audio.src = currentTrack.audioUrl;
        const savedCurrentTime = getSavedPlaybackPosition(currentTrack.id);

        function restoreSavedPosition() {
            if (restoredPositionTrackRef.current === currentTrack.id) {
                return;
            }

            if (
                savedCurrentTime > 0 &&
                audio.duration &&
                savedCurrentTime < audio.duration - 5
            ) {
                restoredPositionTrackRef.current = currentTrack.id;

                restoreTimeout = window.setTimeout(() => {
                    restoringPositionRef.current = true;
                    audio.currentTime = savedCurrentTime;

                    const restoredPercent = Math.min(
                        100,
                        Math.max(0, (savedCurrentTime / audio.duration) * 100)
                    );

                    setProgress(restoredPercent);
                    setTimeCode({
                        current: formatTime(savedCurrentTime),
                        duration: formatTime(audio.duration),
                    });

                    savePlaybackPosition(savedCurrentTime);
                    restoreGuardTimeout = window.setTimeout(() => {
                        restoringPositionRef.current = false;
                    }, 500);
                }, 150);
            }
        }

        audio.addEventListener('loadedmetadata', restoreSavedPosition, {
            once: true,
        });
        audio.load();

        if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: currentTrack.title || '',
                artist: currentTrack.artist || '',
                album: 'De Crop Over Larder',
                artwork: currentTrack.cover
                    ? [
                        {
                            src: currentTrack.cover,
                            sizes: '512x512',
                            type: 'image/jpeg',
                        },
                    ]
                    : [],
            });
        }

        if (!castConnected) {
            setProgress(0);
            setTimeCode({ current: '0:00', duration: '0:00' });
        }
        setHasSeeked(false);
        resetListenSession();

        startListenSession(currentTrack, playbackId, listenController.signal);

        if (currentTrack.isFeatured) {
            trackEvent('featured_track_play', {
                track_id: currentTrack.id,
                track_title: currentTrack.title,
                genre: currentTrack.genre || 'unknown',
                source_type: type || 'direct',
                source_name: playlistTitle || '',
            });
        }

        trackEvent('track_play', {
            track_id: currentTrack.id,
            track_title: currentTrack.title,
            genre: currentTrack.genre || 'unknown',
            source_type: type || 'direct',
            source_name: playlistTitle || '',
        });

        if (castConnected) {
            audio.pause();
        } else if (isPlaying) {
            audio.play().catch(() => { });
        }

        return () => {
            listenController.abort();
            audio.removeEventListener('loadedmetadata', restoreSavedPosition);
            if (restoreTimeout) window.clearTimeout(restoreTimeout);
            if (restoreGuardTimeout) window.clearTimeout(restoreGuardTimeout);
            restoringPositionRef.current = false;
        };
    }, [audioRef, currentTrack?.id, currentTrack?.audioUrl, playbackId]);

    useEffect(() => {
        if (!('mediaSession' in navigator)) {
            return;
        }

        navigator.mediaSession.setActionHandler('play', () => {
            if (castConnected) {
                toggleCastPlayback(true);
            } else {
                play();
            }
        });

        navigator.mediaSession.setActionHandler('pause', () => {
            if (castConnected) {
                toggleCastPlayback(false);
            } else {
                togglePlay();
            }
        });

        navigator.mediaSession.setActionHandler('nexttrack', () => {
            handleNext();
        });

        navigator.mediaSession.setActionHandler('previoustrack', () => {
            handlePrevious();
        });

    }, [
        castConnected,
        currentTrack?.id,
        toggleCastPlayback,
    ]);

    useEffect(() => {
        const audio = audioRef.current;

        if (!audio) {
            return;
        }

        if (castConnected) {
            audio.pause();
            return;
        }

        if (isPlaying && currentTrack?.audioUrl) {
            audio.play().catch(() => { });
        } else {
            audio.pause();
        }

        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState =
                isPlaying ? 'playing' : 'paused';
        }
    }, [audioRef, isPlaying, currentTrack?.audioUrl, castConnected]);

    useEffect(() => {
        if (!isPlaying || !currentTrack?.id || !listenId || !listenToken) {
            return;
        }

        progressTimerRef.current = window.setInterval(() => {
            const percent = castConnected ? progress : getCurrentPercent();

            if (percent <= 0 || listenCompleted || completeRequestSentRef.current) {
                return;
            }

            const nextMilestone = getNextProgressMilestone(percent);

            if (!nextMilestone) {
                return;
            }

            progressMilestonesRef.current.add(nextMilestone);
            updateListenProgress(nextMilestone);
        }, 5000);

        return () => {
            window.clearInterval(progressTimerRef.current);
        };
    }, [
        isPlaying,
        castConnected,
        progress,
        currentTrack?.id,
        listenId,
        listenToken,
        hasSeeked,
        listenCompleted,
        castConnected,
    ]);

    useEffect(() => {
        function handlePageExit() {
            const latestTime = castConnected
                ? latestPlaybackTimeRef.current
                : audioRef.current?.currentTime || 0;

            if (latestTime > 0) {
                savePlaybackPosition(latestTime);
            }

            sendFinalProgress();
        }

        window.addEventListener('pagehide', handlePageExit);
        window.addEventListener('beforeunload', handlePageExit);

        return () => {
            window.removeEventListener('pagehide', handlePageExit);
            window.removeEventListener('beforeunload', handlePageExit);
        };
    }, [
        currentTrack?.id,
        listenId,
        listenToken,
        maxPercent,
        hasSeeked,
        listenCompleted,
    ]);

    useEffect(() => {
        const root = document.documentElement;

        if (!isOpen) {
            root.style.setProperty('--bjnt-player-height', '0px');
            return;
        }

        const updateHeight = () => {
            const height = playerRef.current?.offsetHeight || 0;

            root.style.setProperty(
                '--bjnt-player-height',
                `${height}px`
            );
        };

        updateHeight();

        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, [isOpen, currentTrack?.id]);

    useEffect(() => {
        return () => {
            document.documentElement.style.setProperty(
                '--bjnt-player-height',
                '0px'
            );
        };
    }, []);

    function handleTimeUpdate() {
        if (castConnected) {
            return;
        }

        const percent = getCurrentPercent();
        const nextMaxPercent = getNextMaxPercent(percent);

        if (nextMaxPercent > (maxPercent || 0)) {
            updateListenSession({
                maxPercent: nextMaxPercent,
            });
        }

        if (
            percent >= 90 &&
            !hasSeeked &&
            !listenCompleted &&
            !completeRequestSentRef.current
        ) {
            completeRequestSentRef.current = true;
            completeListen(percent, false);

            if (currentTrack.isFeatured) {
                trackEvent('featured_track_listen', {
                    track_id: currentTrack.id,
                    track_title: currentTrack.title,
                    genre: currentTrack.genre || 'unknown',
                    source_type: type || 'direct',
                    source_name: playlistTitle || '',
                });
            }

            trackEvent('track_listen', {
                track_id: currentTrack.id,
                genre: currentTrack.genre || 'unknown',
                source_type: type || 'direct',
                source_name: playlistTitle || '',
            });
        }
    }

    function handleEnded() {
        clearPlaybackPosition();
        setProgress(100);

        if (listenCompleted) {
            updateListenProgress(100, hasSeeked);
        } else {
            completeListen(100, hasSeeked);
        }

        next();
    }

    function handleProgressClick(event) {
        setHasSeeked(true);

        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const ratio = x / rect.width;

        if (castConnected) {
            seekCast(ratio);
            return;
        }

        const audio = audioRef.current;

        if (!audio || !audio.duration) {
            return;
        }

        audio.currentTime = audio.duration * ratio;

        updateListenSession({
            maxPercent: Math.max(maxPercent || 0, getCurrentPercent()),
        });
    }

    async function handleClosePlayer() {
        clearPlaybackPosition();
        sendFinalProgress();

        if (castConnected) {
            await stopCasting({ continueInBrowser: false });
        }

        closePlayer();
    }

    function handleNext() {
        sendFinalProgress();

        trackEvent('track_skip', {
            track_id: currentTrack.id,
            track_title: currentTrack.title,
            direction: 'next',
            time_elapsed: audioRef.current
                ? Math.floor(audioRef.current.currentTime)
                : 0,
            playback_target: castConnected ? 'cast' : 'browser',
        });

        next();
    }

    function getCurrentPlaybackTime() {
        if (castConnected) {
            return latestPlaybackTimeRef.current || 0;
        }

        return audioRef.current?.currentTime || 0;
    }

    function restartCurrentTrack() {
        setHasSeeked(true);
        clearPlaybackPosition();

        if (castConnected) {
            seekCast(0);
            setProgress(0);
            setTimeCode((current) => ({
                ...current,
                current: '0:00',
            }));
            return;
        }

        const audio = audioRef.current;

        if (!audio) {
            return;
        }

        audio.currentTime = 0;
        setProgress(0);
        setTimeCode((current) => ({
            ...current,
            current: '0:00',
        }));
    }

    function handlePrevious() {
        const now = Date.now();

        if (now < ignorePreviousClickUntilRef.current) {
            return;
        }

        const currentTime = getCurrentPlaybackTime();
        const isDoubleClick =
            now - lastPreviousClickAtRef.current <= previousDoubleClickWindow;

        lastPreviousClickAtRef.current = now;

        if (
            currentTime > previousRestartThreshold &&
            !isDoubleClick
        ) {
            trackEvent('track_restart', {
                track_id: currentTrack.id,
                track_title: currentTrack.title,
                time_elapsed: Math.floor(currentTime),
                playback_target: castConnected ? 'cast' : 'browser',
            });

            restartCurrentTrack();
            return;
        }

        if (!hasPrevious) {
            restartCurrentTrack();
            return;
        }

        ignorePreviousClickUntilRef.current = now + previousDoubleClickWindow;

        sendFinalProgress();

        trackEvent('track_skip', {
            track_id: currentTrack.id,
            track_title: currentTrack.title,
            direction: 'prev',
            time_elapsed: Math.floor(currentTime),
            playback_target: castConnected ? 'cast' : 'browser',
        });

        previous();
    }

    if (!isOpen || !currentTrack) {
        return null;
    }

    const isPlaylist = type === 'playlist';
    const playerIsPlaying = castConnected
        ? castIsPlaying
        : airPlayConnected
            ? airPlayIsPlaying
            : isPlaying;
    return (
        <div
            ref={playerRef}
            className={castConnected ? 'bjnt-player bjnt-player--casting' : 'bjnt-player'}>
            <audio
                ref={audioRef}
                x-webkit-airplay="allow"
                controlsList="nodownload"
                onTimeUpdate={handleTimeUpdate}
                onSeeking={() => {
                    if (!restoringPositionRef.current) setHasSeeked(true);
                }}
                onSeeked={() => {
                    restoringPositionRef.current = false;
                }}
                onEnded={handleEnded}
            />

            <div className="bjnt-player-inner">
                <div className="bjnt-player-cover">
                    {currentTrack.cover ? (
                        <img src={currentTrack.cover} alt="" />
                    ) : (
                        <span />
                    )}
                </div>

                <div className="bjnt-player-main">
                    {isPlaylist && (
                        <div className="bjnt-player-context">
                            <span>{castConnected ? 'Casting from' : 'Playing from'}</span>
                            <strong>{playlistTitle}</strong>
                            {playlistSubtitle && <em>{playlistSubtitle}</em>}
                        </div>
                    )}

                    {castConnected && (
                        <div className="bjnt-player-casting-status">
                            <Icon name="cast" size={14} />
                            <span>Casting to TV</span>
                        </div>
                    )}

                    <div className="bjnt-player-title-row">
                        <div>
                            <Link to={currentTrack.url} className="bjnt-player-title">
                                {currentTrack.title}
                            </Link>

                            <div className="bjnt-player-artist">
                                {currentTrack.artist}
                            </div>
                        </div>

                        {isPlaylist && (
                            <div className="bjnt-player-count">
                                {currentIndex + 1} / {tracks.length}
                            </div>
                        )}
                    </div>

                    <div className="bjnt-player-timeline-wrapper">
                        <span className="bjnt-player-time-text">{timeCode.current}</span>

                        <button
                            type="button"
                            className="bjnt-player-progress"
                            onClick={handleProgressClick}
                            aria-label="Seek track"
                        >
                            <span
                                className="bjnt-player-progress-fill"
                                style={{ transform: `scaleX(${progress / 100})` }}
                            />
                        </button>

                        <span className="bjnt-player-time-text">{timeCode.duration}</span>
                    </div>
                </div>

                <div className="bjnt-player-controls">
                    <button
                        type="button"
                        onClick={handlePrevious}
                        disabled={!hasPrevious && progress <= 0}
                        aria-label="Previous track"
                    >
                        <Icon name="prev" />
                    </button>

                    <button
                        type="button"
                        className="bjnt-player-play"
                        onClick={() => {
                            const eventName = playerIsPlaying ? 'track_pause' : 'track_resume';

                            trackEvent(eventName, {
                                track_id: currentTrack.id,
                                track_title: currentTrack.title,
                                genre: currentTrack.genre || 'unknown',
                                current_time: audioRef.current
                                    ? Math.floor(audioRef.current.currentTime)
                                    : 0,
                                playback_target: castConnected ? 'cast' : 'browser',
                            });

                            if (castConnected) {
                                toggleCastPlayback();
                            } else {
                                togglePlay();
                            }
                        }}
                        aria-label={playerIsPlaying ? 'Pause' : 'Play'}
                    >
                        <Icon name={playerIsPlaying ? 'pause' : 'play'} size={24} />
                    </button>

                    <button
                        type="button"
                        onClick={handleNext}
                        disabled={!hasNext}
                        aria-label="Next track"
                    >
                        <Icon name="next" />
                    </button>

                    {tracks.length > 1 && (
                        <button
                            type="button"
                            className="bjnt-player-queue-toggle"
                            onClick={() => setIsQueueOpen(true)}
                            aria-label="Open queue"
                        >
                            ☰
                        </button>
                    )}

                    <ShareButton
                        track={{
                            id: currentTrack.id,
                            title: currentTrack.title,
                            artist: currentTrack.artist,
                            url: currentTrack.url,
                        }}
                        location="music_player"
                        className="bjnt-player-share"
                    />

                    <button
                        type="button"
                        className="bjnt-player-close"
                        onClick={handleClosePlayer}
                        aria-label="Close player"
                    >
                        ×
                    </button>
                </div>

                {tracks.length > 1 && isQueueOpen && (
                    <div className="bjnt-player-queue-drawer">
                        <button
                            type="button"
                            className="bjnt-player-queue-backdrop"
                            onClick={() => setIsQueueOpen(false)}
                            aria-label="Close queue"
                        />

                        <div className="bjnt-player-queue-panel">
                            <div className="bjnt-player-queue__header">
                                <div>
                                    <strong>Up Next</strong>
                                    <span>{currentIndex + 1} of {tracks.length} tracks</span>
                                </div>

                                <button
                                    type="button"
                                    className="bjnt-player-queue__close"
                                    onClick={() => setIsQueueOpen(false)}
                                    aria-label="Close queue"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="bjnt-player-queue__list">
                                {tracks.map((track, index) => (
                                    <div
                                        key={`${track.id}-${index}`}
                                        className={
                                            index === currentIndex
                                                ? 'bjnt-player-queue__item bjnt-player-queue__item--active'
                                                : 'bjnt-player-queue__item'
                                        }
                                    >
                                        <button
                                            type="button"
                                            onClick={() => playQueueIndex(index)}
                                            disabled={index === currentIndex}
                                        >
                                            <span className="bjnt-player-queue__cover">
                                                {track.cover ? <img src={track.cover} alt="" /> : null}
                                            </span>

                                            <span className="bjnt-player-queue__text">
                                                {index === currentIndex && (
                                                    <span className="bjnt-player-queue__status">
                                                        Now Playing
                                                    </span>
                                                )}

                                                <strong>{track.title}</strong>
                                                <em>{track.artist}</em>
                                            </span>
                                        </button>

                                        {index !== currentIndex && (
                                            <button
                                                type="button"
                                                className="bjnt-player-queue__remove"
                                                onClick={() => removeFromQueue(index)}
                                                aria-label={`Remove ${track.title} from queue`}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
